import React, {
  ComponentProps,
  ComponentType,
  useCallback,
  useMemo
} from 'react';
import styled from 'styled-components';

import ASwitch from 'antd/lib/switch';

import { Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { Hint } from './Hint';

type Props = ComponentProps<typeof ASwitch> & {
  label?: React.ReactNode;
  value: boolean;
  hint?: string;
  confirm?: string;
};

function SwitchBase({
  label,
  value,
  checked,
  className,
  hint,
  ...props
}: Props) {
  return (
    <label className={className}>
      {label && (
        <>
          <span className="label">
            {label}
            {hint && <Hint hint={hint} />}
          </span>
          &nbsp;
        </>
      )}
      <ASwitch {...props} checked={checked ?? value} />
    </label>
  );
}

function withConfirm(Component: ComponentType<Props>) {
  function ComposedSwitch({
    value,
    confirm,
    onChange,
    checked,
    ...props
  }: Props) {
    const shouldConfirm = useMemo(
      () => !(value ?? checked) && Boolean(confirm),
      [value, confirm, checked]
    );
    const { t } = useTranslation('ui');
    const handleConfirm = useCallback(
      () => onChange?.(true, null!),
      [onChange]
    );
    const handleDecline = useCallback(
      () => onChange?.(false, null!),
      [onChange]
    );

    if (!shouldConfirm)
      return (
        <Component
          {...props}
          value={value}
          onChange={onChange}
          checked={checked}
        />
      );

    return (
      <Popconfirm
        title={confirm}
        okText={t('yes')}
        cancelText={t('no')}
        onConfirm={handleConfirm}
        onCancel={handleDecline}
      >
        <Component
          value={value}
          {...props}
          onChange={undefined}
          checked={checked}
        />
      </Popconfirm>
    );
  }
  ComposedSwitch.displayName = 'SwitchWithConfirm';
  return ComposedSwitch;
}

const Switch = withConfirm(SwitchBase);

export default styled(Switch)`
  cursor: pointer;
  display: inline-block;
  margin-bottom: 10px;
  align-self: flex-start;
  display: flex;
  align-items: center;
  .label {
    display: flex;
    align-items: center;
    .anticon {
      color: #4f637e;
      font-size: 1.4em;
      margin-left: 0.5em;
    }
  }
  @media (min-width: 781px) {
    flex-direction: row-reverse;
    justify-content: flex-end;
    .ant-switch {
      margin-right: 1em;
    }
  }
  @media (max-width: 780px) {
    justify-content: space-between;
    width: 100%;
  }
`;
